import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { playClickSound } from "@/lib/sounds";
import { useAdminPendingCount } from "@/hooks/useAdminPendingCount";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Home,
  PlayCircle,
  BookOpen,
  History,
  LogOut,
  ArrowLeftRight,
  Settings,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import brandLogoHorizontal from "@/assets/brand/nivela-logo-horizontal.png";
import brandIcon from "@/assets/brand/nivela-icon.png";

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

const menuItems: MenuItem[] = [
  { title: "Home", url: "/videoaulas", icon: Home },
  { title: "Catálogo", url: "/videoaulas/catalogo", icon: PlayCircle },
  { title: "Meus Cursos", url: "/videoaulas/meus-cursos", icon: BookOpen },
  { title: "Histórico", url: "/videoaulas/historico", icon: History },
  { title: "Estatísticas", url: "/videoaulas/estatisticas", icon: BarChart3 },
];

export function VideoaulasSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { isAdmin, pendingCount } = useAdminPendingCount();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { profile, userInitials } = useProfile();


  const handleSignOut = async () => {
    try { await supabase.auth.signOut(); } catch (err) { console.warn('signOut failed', err); }
    navigate("/");
  };

  const isActive = (url: string) => location.pathname === url;

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      <SidebarHeader className="border-b border-border/50 p-4">
        <div className="flex items-center gap-3">
          {collapsed ? (
            <img src={brandIcon} alt="Nível A Educação" className="h-9 w-9 rounded-xl flex-shrink-0" />
          ) : (
            <div className="flex flex-col gap-0.5">
              <img src={brandLogoHorizontal} alt="Nível A Educação" className="h-7 w-auto" />
              <span className="text-xs text-muted-foreground">Videoaulas</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="flex flex-col px-2 py-4">
        <SidebarGroup className="px-2">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {menuItems.map((item, index) => {
                const active = isActive(item.url);
                return (
                  <SidebarMenuItem
                    key={item.url}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <a
                        href={item.url}
                        onClick={(e) => {
                          e.preventDefault();
                          playClickSound();
                          navigate(item.url);
                        }}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-full transition-all duration-200",
                          active
                            ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium"
                            : "hover:bg-muted/60"
                        )}
                      >
                        <item.icon
                          className={cn(
                            "h-4 w-4 flex-shrink-0",
                            active && "scale-110"
                          )}
                        />
                        {!collapsed && (
                          <span className="truncate">{item.title}</span>
                        )}
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter className="border-t border-border/50 p-4">
        {/* Trocar Módulo */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Trocar Módulo">
              <a
                href="/select-module"
                onClick={(e) => { e.preventDefault(); navigate("/select-module"); }}
                className={cn(
                  "flex items-center rounded-full border border-border bg-muted/50 hover:bg-muted transition-all duration-200 shadow-sm hover:shadow-md mb-2",
                  collapsed ? "justify-center w-10 h-10 p-0 mx-auto" : "gap-3 px-3 py-2.5"
                )}
              >
                <ArrowLeftRight className={cn("flex-shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")} />
                {!collapsed && <span className="font-medium">Trocar Módulo</span>}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {isAdmin && (
          <NavLink to="/admin" className="block px-2 mb-2" activeClassName="">
            <div className="relative flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2 hover:bg-muted transition-colors">
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              {!collapsed && <span className="text-xs text-muted-foreground">Painel Admin</span>}
              {pendingCount > 0 && (
                collapsed ? (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-destructive" />
                ) : (
                  <span className="ml-auto bg-destructive text-destructive-foreground rounded-full px-1.5 py-0.5 text-[10px] font-medium min-w-[18px] text-center">
                    {pendingCount}
                  </span>
                )
              )}
            </div>
          </NavLink>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <div
              className={cn(
                "flex items-center w-full",
                collapsed ? "flex-col gap-2" : "gap-2"
              )}
            >
              <SidebarMenuButton
                tooltip={profile?.first_name || "Perfil"}
                className="flex-1 min-w-0 overflow-visible"
                onClick={() => navigate("/profile")}
              >
                <div className="flex items-center gap-3 w-full group">
                  <Avatar className="h-9 w-9 flex-shrink-0 ring-2 ring-border transition-all duration-200 group-hover:ring-foreground/20">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-gradient-to-br from-purple-600 to-pink-600 text-white text-sm font-medium">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  {!collapsed && (
                    <div className="flex flex-col items-start text-left min-w-0">
                      <span className="text-sm font-medium truncate w-full">
                        {profile?.first_name || "Usuário"}
                      </span>
                      <span className="text-xs text-muted-foreground truncate w-full">
                        {user?.email}
                      </span>
                    </div>
                  )}
                </div>
              </SidebarMenuButton>
              <button
                onClick={handleSignOut}
                className="p-2 rounded-xl hover:bg-muted/60 transition-all duration-200 text-destructive flex-shrink-0"
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
